import { useState } from 'react';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';
import { Button, Table } from 'react-bootstrap';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { OrdersTablePaging } from './OrdersTablePaging';
import { OrdersTableFilterControl } from './OrdersTableFilterControl';
import { ParxlTemplateModal } from './ParxlTemplateModal';
import { YunExportModal } from './YunExportModal';
import { ImportTrackingModal } from './ImportTrackingModal';
import type { Column } from './Columns';
import { Columns as allColumns } from './Columns';

interface OrdersTableProps {
  orders: OrderSummary[];
  isLoading?: boolean;
  // Filters (owned by OrdersPage)
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  country: string;
  onCountryChange: (country: string) => void;
  status: string;
  onStatusChange: (status: string) => void;
  itemsPerPage: string;
  onItemsPerPageChange: (value: string) => void;
  // Search
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  onSearchSubmit: () => void;
  onSearchClear: () => void;
  picValue: string;
  onPicValueChange: (value: string) => void;
  onPicSubmit: () => void;
  onPicClear: () => void;
  hasOrderNumber: boolean;
  onHasOrderNumberChange: (value: boolean) => void;
  hasTrackingNumber: boolean;
  onHasTrackingNumberChange: (value: boolean) => void;
  // Cursor pagination
  pageNumber: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  // Total across all pages for the current query (null = unknown).
  totalCount: number | null;
  // Multi-select for merging orders (owned by OrdersPage)
  selectedOrderIds: string[];
  onToggleOrderSelect: (orderId: string) => void;
  onMergeClick: () => void;
}

// Presentational only: renders exactly the page of orders it receives. No client-side
// filtering, sorting, or slicing — the server owns ordering (created desc) and pagination.
export function OrdersTable({
  orders,
  isLoading,
  startDate,
  endDate,
  onDateRangeChange,
  country,
  onCountryChange,
  status,
  onStatusChange,
  itemsPerPage,
  onItemsPerPageChange,
  searchValue,
  onSearchValueChange,
  onSearchSubmit,
  onSearchClear,
  picValue,
  onPicValueChange,
  onPicSubmit,
  onPicClear,
  hasOrderNumber,
  onHasOrderNumberChange,
  hasTrackingNumber,
  onHasTrackingNumberChange,
  pageNumber,
  hasPrevPage,
  hasNextPage,
  onPrevPage,
  onNextPage,
  totalCount,
  selectedOrderIds,
  onToggleOrderSelect,
  onMergeClick,
}: OrdersTableProps) {
  const [visibleColumns, setVisibleColumns] = useState<Column[]>(allColumns.filter((col) => col.isVisible));
  const [showParxlModal, setShowParxlModal] = useState(false);
  const [showYunModal, setShowYunModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleColumnVisibility = (column: Column, isVisible: boolean) => {
    if (column.isRequired) {
      return; // Don't allow hiding required columns
    }

    setVisibleColumns((prev) => {
      if (isVisible) {
        if (!prev.some((col) => col.accessor === column.accessor)) {
          // Re-derive from allColumns so column order is preserved.
          return allColumns.filter(
            (col) =>
              col.isRequired || prev.some((p) => p.accessor === col.accessor) || col.accessor === column.accessor,
          );
        }
        return prev;
      }
      return prev.filter((col) => col.accessor !== column.accessor);
    });
  };

  // Parse a 'YYYY-MM-DD' string as a local calendar date. `new Date('YYYY-MM-DD')` parses as
  // UTC midnight, which renders the previous day in negative-offset (e.g. US) time zones.
  const parseLocalDate = (dateStr: string): Date | null => {
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) {
      return null;
    }
    return new Date(y, m - 1, d);
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) {
      return '';
    }
    const start = parseLocalDate(startDate)?.toLocaleDateString() ?? startDate;
    const end = parseLocalDate(endDate)?.toLocaleDateString() ?? endDate;
    return `${start} to ${end}`;
  };

  return (
    <div className="space-y-4 flex flex-col items-center">
      <OrdersTableFilterControl
        allColumns={allColumns}
        visibleColumns={visibleColumns}
        onColumnVisibilityChange={handleColumnVisibility}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={onDateRangeChange}
        country={country}
        onCountryChange={onCountryChange}
        status={status}
        onStatusChange={onStatusChange}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={onItemsPerPageChange}
        searchValue={searchValue}
        onSearchValueChange={onSearchValueChange}
        onSearchSubmit={onSearchSubmit}
        onSearchClear={onSearchClear}
        picValue={picValue}
        onPicValueChange={onPicValueChange}
        onPicSubmit={onPicSubmit}
        onPicClear={onPicClear}
        hasOrderNumber={hasOrderNumber}
        onHasOrderNumberChange={onHasOrderNumberChange}
        hasTrackingNumber={hasTrackingNumber}
        onHasTrackingNumberChange={onHasTrackingNumberChange}
        onOpenParxlModal={() => setShowParxlModal(true)}
        onOpenYunModal={() => setShowYunModal(true)}
        onOpenImportModal={() => setShowImportModal(true)}
      />

      <ParxlTemplateModal show={showParxlModal} onHide={() => setShowParxlModal(false)} />
      <YunExportModal show={showYunModal} onHide={() => setShowYunModal(false)} />
      <ImportTrackingModal show={showImportModal} onHide={() => setShowImportModal(false)} />

      <p className="text-muted-foreground p-2">
        {`${totalCount !== null ? `${totalCount} total` : '- total'} · ${formatDateRange()}`}
      </p>

      <div className="flex items-center gap-3 w-full">
        <Button variant="primary" size="sm" onClick={onMergeClick} disabled={selectedOrderIds.length < 2}>
          Merge Orders{selectedOrderIds.length > 0 ? ` (${selectedOrderIds.length})` : ''}
        </Button>
      </div>

      <div className="rounded-md border mt-3 w-full">
        <Table hover responsive className="align-middle mb-0">
          <TableHeader>
            <TableRow className="h-12">
              <TableHead className="p-0 select-none w-10">
                <div className="flex items-center justify-center px-2" />
              </TableHead>
              {visibleColumns.map((column) => (
                <TableHead
                  key={String(column.accessor)}
                  className="p-0 select-none"
                  style={column.accessor === 'lineItems.sourcingInfo' ? { width: '300px' } : undefined}
                >
                  <div className="flex items-center gap-1 px-2">{column.header}</div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} className="h-12 text-center py-3">
                  Loading...
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} className="h-12 text-center py-3">
                  No orders found.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.orderId} className="hover:bg-muted/50 h-12">
                  <TableCell className="py-3 text-center">
                    <input
                      type="checkbox"
                      aria-label={`Select order ${order.orderId}`}
                      checked={selectedOrderIds.includes(order.orderId)}
                      onChange={() => onToggleOrderSelect(order.orderId)}
                    />
                  </TableCell>
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={`${order.orderId}-${String(column.accessor)}`}
                      className="py-3"
                      style={column.accessor === 'lineItems.sourcingInfo' ? { width: '300px' } : undefined}
                    >
                      {column.cell ? column.cell(order) : (order as any)[column.accessor]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <OrdersTablePaging
        pageNumber={pageNumber}
        hasPrevPage={hasPrevPage}
        hasNextPage={hasNextPage}
        onPrevPage={onPrevPage}
        onNextPage={onNextPage}
      />
    </div>
  );
}
